import { type ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './auth/AuthContext';
import { useAuth } from './auth/useAuth';
import { ToastProvider } from './components/ui/Toast';
import { AppShell } from './components/shell/AppShell';
import { LoginPage } from './login/LoginPage';
import { CredentialsForm } from './login/CredentialsForm';
import { MfaChallengePage } from './login/MfaChallengePage';
import { MfaEnrollmentPage } from './login/MfaEnrollmentPage';
import { RecoveryCodesPage } from './login/RecoveryCodesPage';
import { CredentialsPage } from './pages/credentials/CredentialsPage';
import { CredentialDetailPage } from './pages/credentials/CredentialDetailPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { ReportDetailPage } from './pages/reports/ReportDetailPage';
import { UsersPage } from './pages/users/UsersPage';
import { UserDetailPage } from './pages/users/UserDetailPage';
import { SettingsPage } from './pages/settings/SettingsPage';

const StyleguidePage = import.meta.env.DEV
  ? lazy(() => import('./pages/styleguide/StyleguidePage').then((m) => ({ default: m.StyleguidePage })))
  : null;

function RequireAuth({ children }: { children: ReactNode }) {
  const { tokens } = useAuth();
  if (!tokens) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-base, #f9f5ef)',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          padding: '2rem',
          background: 'var(--surface-raised, #fff)',
          border: '1px solid var(--border-danger, #fca5a5)',
          borderRadius: 8,
          textAlign: 'center',
        }}
      >
        <p style={{ color: 'var(--text-danger, #b91c1c)', marginBottom: '0.5rem', fontWeight: 600 }}>
          Algo salió mal
        </p>
        <p style={{ color: 'var(--text-secondary, #5c4f44)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
          {error.message}
        </p>
        <button
          onClick={resetError}
          style={{
            padding: '0.5rem 1.25rem',
            background: 'var(--color-terra-600, #b05a3a)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />}>
        <Route index element={<CredentialsForm />} />
        <Route path="mfa-challenge" element={<MfaChallengePage />} />
        <Route path="mfa-enroll" element={<MfaEnrollmentPage />} />
        <Route path="recovery-codes" element={<RecoveryCodesPage />} />
      </Route>

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/credentials" replace />} />
        <Route path="/credentials" element={<CredentialsPage />} />
        <Route path="/credentials/:id" element={<CredentialDetailPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:id" element={<ReportDetailPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:id" element={<UserDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {import.meta.env.DEV && StyleguidePage && (
          <Route
            path="/styleguide"
            element={
              <Suspense fallback={null}>
                <StyleguidePage />
              </Suspense>
            }
          />
        )}
      </Route>

      <Route path="*" element={<Navigate to="/credentials" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <ErrorFallback error={error as Error} resetError={resetError} />
    )}>
      <AuthProvider>
        <BrowserRouter>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </BrowserRouter>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
